import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SchedeEvento } from '../components/eventi/SchedeEvento';
import { eScheda, type SchedaEvento } from '../components/eventi/schede';
import { TestataEvento, TornaAgliEventi } from '../components/eventi/TestataEvento';
import { DaCompletare } from '../components/eventi/DaCompletare';
import { SchedaProgramma } from '../components/eventi/SchedaProgramma';
import { SchedaImpostazioni } from '../components/eventi/SchedaImpostazioni';
import { SchedaRichieste } from '../components/eventi/SchedaRichieste';
import { SchedaAssegnazioni } from '../components/eventi/SchedaAssegnazioni';
import { SchedaBiglietti } from '../components/eventi/SchedaBiglietti';
import { useEvento } from '../components/eventi/useEvento';
import { programmaPronto } from '../lib/eventi/programma';
import { cosaManca, riassuntoSchede } from '../lib/eventi/riepilogo';
import { contaBigliettiEvento, riassuntoBiglietti } from '../lib/eventi/biglietti';
import { contaStati } from '../lib/eventi/stati';

/**
 * La pagina di un evento: la testata (con l'interruttore delle richieste),
 * cosa manca per partire, e cinque schede nell'ordine in cui si lavora:
 * Programma, Biglietti, Richieste, Assegnazioni, Impostazioni. La scheda aperta sta
 * nell'indirizzo (?scheda=...): tornando indietro o ricaricando si ritrova.
 */
export default function EventiDettaglioPage() {
  const { id } = useParams<{ id: string }>();
  const [parametri, setParametri] = useSearchParams();
  const {
    evento,
    programma,
    partecipanti,
    etichette,
    biglietti,
    statoLettura,
    erroreBiglietti,
    caricando,
    errore,
    caricaTutto,
    ricaricaEvento,
    ricaricaPartecipanti,
    ricaricaBiglietti,
  } = useEvento(id);
  const conti = useMemo(() => contaStati(partecipanti), [partecipanti]);

  if (caricando && !evento) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-xl bg-gray-100 animate-pulse" />
          <div className="h-9 w-72 rounded-lg bg-gray-100 animate-pulse" />
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-gray-50 animate-pulse" />
          ))}
        </div>
        <div className="h-96 rounded-xl bg-gray-50 animate-pulse" />
      </div>
    );
  }

  if (errore || !evento || !programma) {
    return (
      <div className="space-y-4">
        <TornaAgliEventi />
        <Card className="border-red-200 bg-red-50/40">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-red-700 font-medium">Non riesco ad aprire l'evento</p>
            <p className="text-xs text-neutral-500 mt-1 max-w-md mx-auto">{errore ?? 'Evento non trovato.'}</p>
            <Button variant="outline" className="mt-4" onClick={() => void caricaTutto()}>
              Riprova
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const mancanze = cosaManca(evento, programma);
  const riassunti = {
    ...riassuntoSchede(programma, conti, mancanze.filter((m) => m.scheda === 'impostazioni').length),
    biglietti: riassuntoBiglietti((evento.prodotti_sito ?? []).length > 0, contaBigliettiEvento(biglietti)),
  };
  // Si apre dove c'e' da fare: prima il programma, poi la lista, poi le richieste.
  const predefinita: SchedaEvento = !programmaPronto(programma) ? 'programma' : !evento.lista_id ? 'impostazioni' : 'richieste';
  const richiesta = parametri.get('scheda');
  const scheda: SchedaEvento = eScheda(richiesta) ? richiesta : predefinita;
  const apri = (s: SchedaEvento) => setParametri({ scheda: s }, { replace: true });

  return (
    <div className="space-y-5 md:space-y-6">
      <TestataEvento evento={evento} programma={programma} partecipanti={partecipanti.length} onEventoCambiato={ricaricaEvento} />
      <DaCompletare mancanze={mancanze} onApri={apri} />
      <SchedeEvento attiva={scheda} onCambia={apri} riassunti={riassunti} />

      {scheda === 'programma' && (
        <SchedaProgramma evento={evento} programma={programma} partecipanti={partecipanti} onEventoCambiato={ricaricaEvento} />
      )}
      {scheda === 'biglietti' && (
        <SchedaBiglietti
          evento={evento}
          biglietti={biglietti}
          statoLettura={statoLettura}
          errore={erroreBiglietti}
          onRicarica={async () => {
            await Promise.all([ricaricaBiglietti(), ricaricaPartecipanti()]);
          }}
          onApri={apri}
        />
      )}
      {scheda === 'richieste' && <SchedaRichieste programma={programma} partecipanti={partecipanti} onApri={apri} />}
      {scheda === 'assegnazioni' && (
        <SchedaAssegnazioni
          evento={evento}
          programma={programma}
          partecipanti={partecipanti}
          onPartecipantiCambiati={ricaricaPartecipanti}
          onApri={apri}
        />
      )}
      {scheda === 'impostazioni' && (
        <SchedaImpostazioni
          evento={evento}
          etichette={etichette}
          onEventoCambiato={ricaricaEvento}
          onPartecipantiCambiati={ricaricaPartecipanti}
        />
      )}
    </div>
  );
}
